import { memo, useMemo } from "react";
import { getChordDetails } from "@/music/chords/chordTheoryEngine";

const X_POSITIONS = [30, 50, 70, 90, 110, 130];
const shape = (frets, fingers, startFret = 1, barres = []) => ({ frets, fingers, startFret, barres });

export const GUITAR_VOICINGS = {
  C_major: shape([null, 3, 2, 0, 1, 0], [null, 3, 2, null, 1, null]),
  C_minor: shape([null, 3, 5, 5, 4, 3], [null, 1, 3, 4, 2, 1], 3, [{ fret: 3, from: 1, to: 5, finger: 1 }]),
  "C#_major": shape([null, 4, 6, 6, 6, 4], [null, 1, 2, 3, 4, 1], 4, [{ fret: 4, from: 1, to: 5, finger: 1 }]),
  "C#_minor": shape([null, 4, 6, 6, 5, 4], [null, 1, 3, 4, 2, 1], 4, [{ fret: 4, from: 1, to: 5, finger: 1 }]),
  D_major: shape([null, null, 0, 2, 3, 2], [null, null, null, 1, 3, 2]),
  D_minor: shape([null, null, 0, 2, 3, 1], [null, null, null, 2, 3, 1]),
  "D#_major": shape([null, 6, 8, 8, 8, 6], [null, 1, 2, 3, 4, 1], 6, [{ fret: 6, from: 1, to: 5, finger: 1 }]),
  "D#_minor": shape([null, 6, 8, 8, 7, 6], [null, 1, 3, 4, 2, 1], 6, [{ fret: 6, from: 1, to: 5, finger: 1 }]),
  E_major: shape([0, 2, 2, 1, 0, 0], [null, 2, 3, 1, null, null]),
  E_minor: shape([0, 2, 2, 0, 0, 0], [null, 2, 3, null, null, null]),
  F_major: shape([1, 3, 3, 2, 1, 1], [1, 3, 4, 2, 1, 1], 1, [{ fret: 1, from: 0, to: 5, finger: 1 }]),
  F_minor: shape([1, 3, 3, 1, 1, 1], [1, 3, 4, 1, 1, 1], 1, [{ fret: 1, from: 0, to: 5, finger: 1 }]),
  "F#_major": shape([2, 4, 4, 3, 2, 2], [1, 3, 4, 2, 1, 1], 2, [{ fret: 2, from: 0, to: 5, finger: 1 }]),
  "F#_minor": shape([2, 4, 4, 2, 2, 2], [1, 3, 4, 1, 1, 1], 2, [{ fret: 2, from: 0, to: 5, finger: 1 }]),
  G_major: shape([3, 2, 0, 0, 0, 3], [2, 1, null, null, null, 3]),
  G_minor: shape([3, 5, 5, 3, 3, 3], [1, 3, 4, 1, 1, 1], 3, [{ fret: 3, from: 0, to: 5, finger: 1 }]),
  "G#_major": shape([4, 6, 6, 5, 4, 4], [1, 3, 4, 2, 1, 1], 4, [{ fret: 4, from: 0, to: 5, finger: 1 }]),
  "G#_minor": shape([4, 6, 6, 4, 4, 4], [1, 3, 4, 1, 1, 1], 4, [{ fret: 4, from: 0, to: 5, finger: 1 }]),
  A_major: shape([null, 0, 2, 2, 2, 0], [null, null, 1, 2, 3, null]),
  A_minor: shape([null, 0, 2, 2, 1, 0], [null, null, 2, 3, 1, null]),
  "A#_major": shape([null, 1, 3, 3, 3, 1], [null, 1, 2, 3, 4, 1], 1, [{ fret: 1, from: 1, to: 5, finger: 1 }]),
  "A#_minor": shape([null, 1, 3, 3, 2, 1], [null, 1, 3, 4, 2, 1], 1, [{ fret: 1, from: 1, to: 5, finger: 1 }]),
  B_major: shape([null, 2, 4, 4, 4, 2], [null, 1, 2, 3, 4, 1], 2, [{ fret: 2, from: 1, to: 5, finger: 1 }]),
  B_minor: shape([null, 2, 4, 4, 3, 2], [null, 1, 3, 4, 2, 1], 2, [{ fret: 2, from: 1, to: 5, finger: 1 }]),
};

const GuitarChordDiagram = memo(({ chordName, root, quality, voicing, capo = 0, compact = false, variant = "dark" }) => {
  const details = useMemo(() => getChordDetails(chordName || `${root || ""}${quality || ""}`), [chordName, quality, root]);
  const diagram = voicing || GUITAR_VOICINGS[`${details.root}_${details.baseQuality}`];
  const resolved = diagram || shape([null, null, null, null, null, null], [null, null, null, null, null, null]);
  const height = compact ? (capo > 0 ? 190 : 172) : (capo > 0 ? 216 : 198);
  const yForFret = (fret) => 38 + (fret - resolved.startFret + 0.5) * 23;

  return (
    <figure className="mx-auto w-fit text-center" aria-label={`Diagrama de guitarra ${details.displayName || "sin acorde"}`}>
      <svg viewBox={`0 0 160 ${height}`} className={compact ? (capo > 0 ? "h-[11.9rem] w-[8.75rem]" : "h-[10.75rem] w-[8.75rem]") : (capo > 0 ? "h-[13.5rem] w-[10rem]" : "h-[12.4rem] w-[10rem]")} role="img">
        <rect x="18" y="26" width="124" height="132" rx="8" fill="#f8fafc" />
        {resolved.frets.map((fret, index) => (
          <text key={`mark-${index}`} x={X_POSITIONS[index]} y="19" textAnchor="middle" fill="#cbd5e1" fontSize="12" fontWeight="600">
            {fret === null ? "X" : fret === 0 ? "O" : ""}
          </text>
        ))}
        {resolved.startFret > 1 && <text x="6" y="53" fill="#a5f3fc" fontSize="10" fontWeight="700">{resolved.startFret}</text>}
        {Array.from({ length: 6 }).map((_, index) => (
          <line key={`fret-${index}`} x1="30" x2="130" y1={38 + index * 23} y2={38 + index * 23} stroke={index === 0 && resolved.startFret === 1 ? "#0f172a" : "#64748b"} strokeWidth={index === 0 && resolved.startFret === 1 ? 4 : 1.2} />
        ))}
        {X_POSITIONS.map((x, index) => (
          <line key={`string-${index}`} x1={x} x2={x} y1="38" y2="153" stroke="#475569" strokeWidth="1.2" />
        ))}
        {resolved.barres.map((barre, index) => (
          <g key={`barre-${index}`}>
            <line x1={X_POSITIONS[barre.from]} x2={X_POSITIONS[barre.to]} y1={yForFret(barre.fret)} y2={yForFret(barre.fret)} stroke="#111827" strokeWidth="13" strokeLinecap="round" />
            <text x={X_POSITIONS[barre.from]} y={yForFret(barre.fret) + 3} textAnchor="middle" fill="#f8fafc" fontSize="8">{barre.finger}</text>
          </g>
        ))}
        {resolved.frets.map((fret, index) => {
          const insideBarre = resolved.barres.some((barre) => barre.fret === fret && index >= barre.from && index <= barre.to);
          if (!Number.isFinite(fret) || fret <= 0 || insideBarre) return null;
          return (
            <g key={`finger-${index}`}>
              <circle cx={X_POSITIONS[index]} cy={yForFret(fret)} r="7.3" fill="#111827" />
              <text x={X_POSITIONS[index]} y={yForFret(fret) + 3} textAnchor="middle" fill="#f8fafc" fontSize="8" fontWeight="700">{resolved.fingers[index]}</text>
            </g>
          );
        })}
        {capo > 0 && <text x="80" y={height - 27} textAnchor="middle" fill={variant === "light" ? "#0f766e" : "#67e8f9"} fontSize="9">Capo {capo}</text>}
        <text x="80" y={height - 8} textAnchor="middle" fill={variant === "light" ? "#0f172a" : "#e2e8f0"} fontSize="15" fontWeight="700">{details.displayName || "--"}</text>
      </svg>
      {!diagram && details.recognized && <figcaption className={`text-[11px] ${variant === "light" ? "text-amber-700" : "text-amber-300"}`}>Forma base no disponible</figcaption>}
    </figure>
  );
});

GuitarChordDiagram.displayName = "GuitarChordDiagram";

export default GuitarChordDiagram;
